/**
 * COLABORADOR do RDO = PROFESSIONAL.
 *
 * User e Professional são entidades diferentes: User serve a login e
 * permissão; Professional é o colaborador real. O apontamento de RDO é de um
 * Professional, então nada aqui olha para User — nem para exigir login, nem
 * para tirar a função do papel de acesso.
 */

export interface CollaboratorSource {
  id: string;
  name: string;
  functionTitle?: string | null;
  /** Vínculo de login. Pode ser null: Professional sem User continua válido. */
  userId?: string | null;
  status?: string | null;
  company?: string | null;
  companyId?: string | null;
  professionalType?: string | null;
  cft?: string | null;
  crea?: string | null;
  snqc?: string | null;
  sncc?: string | null;
}

export interface CollaboratorOption {
  professionalId: string;
  name: string;
  /** Função profissional cadastrada — nunca o papel de acesso do usuário. */
  functionTitle: string;
  /** Registro profissional (CFT/CREA/SNQC/SNCC), quando cadastrado. */
  registration: string;
  company: string;
  professionalType: string;
}

/** Status ativo do domínio real de Professional. */
export const ACTIVE_PROFESSIONAL_STATUS = "ACTIVE";

const text = (value: unknown): string => {
  const raw = value === null || value === undefined ? "" : String(value);
  return raw.trim();
};

/**
 * Função/Cargo do colaborador, sempre do Professional.
 *
 * Campo ausente devolve string vazia — a tela mostra o vazio, em vez de
 * herdar "ADMIN"/"ENGINEER" do papel de acesso ou inventar "INSPETOR".
 */
export function resolveFunctionTitle(professional: CollaboratorSource | null | undefined): string {
  return text(professional?.functionTitle);
}

/** Primeiro registro profissional cadastrado, sem inventar nenhum. */
export function resolveRegistration(professional: CollaboratorSource): string {
  return text(professional.cft) || text(professional.crea) || text(professional.snqc) || text(professional.sncc);
}

/**
 * Colaboradores oferecidos num apontamento novo: ATIVOS, em ordem alfabética.
 *
 * Não há filtro por User: um Professional sem login aparece igual. Também não
 * há filtro por Cliente aqui — o colaborador pertence à equipe/empresa, e o
 * recorte por tenant já acontece na consulta ao repositório.
 */
export function collaboratorOptions(
  professionals: readonly CollaboratorSource[],
  options: { includeInactive?: boolean } = {}
): CollaboratorOption[] {
  return professionals
    .filter(professional => options.includeInactive || text(professional.status) === ACTIVE_PROFESSIONAL_STATUS)
    .map(professional => ({
      professionalId: professional.id,
      name: text(professional.name) || professional.id,
      functionTitle: resolveFunctionTitle(professional),
      registration: resolveRegistration(professional),
      company: text(professional.company),
      professionalType: text(professional.professionalType)
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }));
}

/** Rótulo do seletor: NOME COMPLETO — FUNÇÃO/CARGO. */
export function formatCollaboratorLabel(option: CollaboratorOption): string {
  return option.functionTitle ? `${option.name} — ${option.functionTitle}` : option.name;
}
